import React from "react";
import { Alert, AlertTitle, Snackbar, Typography } from "@mui/material";
import CakeIcon from "@mui/icons-material/Cake";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";

const ONE_HOUR = 60 * 60 * 1000;

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

/**
 * Shows a birthday notification for patients whose birthday is today.
 * Fetches on mount and every hour afterwards. Because the backend only
 * returns people whose birthday is "today", the reminder stops by itself
 * once the day ends (the next fetch returns an empty list).
 *
 * Mount once in a layout that is always visible, e.g. the main dashboard layout.
 */
function BirthdayReminder() {
  const [open, setOpen] = React.useState(false);
  const [dayKey, setDayKey] = React.useState(todayKey());

  const { data, dataUpdatedAt } = useCustomQuery(
    ["BIRTHDAYS_TODAY", dayKey],
    { url: `/patients/birthdays-today`, method: "get" },
    {
      refetchInterval: ONE_HOUR,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );

  const patients = data?.data?.patients || [];

  // Re-open the notification after every successful hourly fetch
  React.useEffect(() => {
    if (patients.length) {
      setOpen(true);
    } else {
      setOpen(false);
    }
    // roll the query key over when the date changes
    if (todayKey() !== dayKey) setDayKey(todayKey());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  if (!patients.length) return null;

  const names = patients
    .map((p) => `${p.firstName} ${p.lastName} (${p.ID}${p.age ? `, turns ${p.age}` : ""})`)
    .join(", ");

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      onClose={(_, reason) => {
        if (reason === "clickaway") return;
        setOpen(false);
      }}
    >
      <Alert
        icon={<CakeIcon fontSize="inherit" />}
        severity="info"
        variant="filled"
        onClose={() => setOpen(false)}
        sx={{ maxWidth: 420 }}
      >
        <AlertTitle>
          Patient Birthday{patients.length > 1 ? "s" : ""} Today 🎂
        </AlertTitle>
        <Typography variant="body2">{names}</Typography>
      </Alert>
    </Snackbar>
  );
}

export default BirthdayReminder;